import React, { useState } from 'react';
import { toast } from 'react-hot-toast';
import jsPDF from 'jspdf';
import { LuX, LuPlus, LuTrash2 } from 'react-icons/lu';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';

// Presupuesto propio (5-oct-2026): PDF simple a nombre del técnico/socio para
// SUS clientes. Sin marca de Dispenser La Tienda y fuera de la liquidación.
// Desde el 12-oct-2026 se guarda (N° correlativo, estado) para volver a bajarlo,
// editarlo o duplicarlo desde la lista del cliente (PresupuestosCliente).
// Sin w-full en la base: en la fila de ítem, w-full le ganaba a w-28 y el campo
// del precio aplastaba al de la descripción (bug 5-oct-2026).
const BASE = 'h-10 px-3 rounded-xl bg-chip text-ink text-body outline-none placeholder:text-muted';
const INPUT = `w-full ${BASE}`;
export const fmt = v => `$ ${Math.round(Number(v) || 0).toLocaleString('es-AR')}`;
const leer = (k, d = '') => { try { return localStorage.getItem(k) || d; } catch { return d; } };
const guardarLS = (k, v) => { try { localStorage.setItem(k, v); } catch {} };
const soloNumero = v => Number(String(v ?? '').replace(/[^\d]/g, '')) || 0;
// "2026-10-12" → "12/10/2026" sin new Date (en Argentina corre un día)
const fechaCorta = f => (f ? String(f).slice(0, 10).split('-').reverse().join('/') : new Date().toLocaleDateString('es-AR'));

/** Arma y descarga el PDF de un presupuesto guardado. */
export function generarPDF(p, cliente) {
    const doc = new jsPDF({ unit: 'mm', format: 'a4' });
    dibujarPresupuesto(doc, p, cliente);
    const nombre = cliente.nombre.toLowerCase().replace(/\s+/g, '-');
    doc.save(`presupuesto${p.numero ? `-${p.numero}` : ''}-${nombre}.pdf`);
}

/**
 * Varios presupuestos en un solo PDF (13-oct-2026), todo seguido en la misma hoja: cada presupuesto
 * con su fecha, cliente, ítems y subtotal, y al final el total general. Pasa de hoja solo si no entra.
 * Si son todos del mismo cliente, el cliente va una sola vez arriba.
 * `lista`: [{ ...presupuesto, cliente: { nombre, direccion } }]
 */
export function generarPDFJunto(lista) {
    const orden = [...lista].sort((a, b) => String(a.fecha).localeCompare(String(b.fecha)) || (a.numero || 0) - (b.numero || 0));
    const doc = new jsPDF({ unit: 'mm', format: 'a4' });
    const M = 18, D = 210 - M; let y = 22;
    const cabe = (alto) => { if (y + alto > 280) { doc.addPage(); y = 22; } };
    const unCliente = new Set(orden.map(p => p.cliente?.nombre)).size === 1 ? orden[0].cliente : null;
    const emisor = orden.find(p => p.emisor)?.emisor || '';
    const contacto = orden.find(p => p.contacto)?.contacto || '';
    const desde = fechaCorta(orden[0].fecha), hasta = fechaCorta(orden[orden.length - 1].fecha);

    doc.setFont('helvetica', 'bold'); doc.setFontSize(18); doc.text('Presupuesto', M, y);
    doc.setFont('helvetica', 'normal'); doc.setFontSize(10);
    doc.text(desde === hasta ? desde : `${desde} al ${hasta}`, D, y, { align: 'right' });
    if (emisor) { y += 8; doc.setFont('helvetica', 'bold'); doc.text(emisor, M, y); }
    if (contacto) { y += 5; doc.setFont('helvetica', 'normal'); doc.text(contacto, M, y); }
    y += 10; doc.setDrawColor(200); doc.line(M, y, D, y); y += 8;
    if (unCliente) {
        doc.setFont('helvetica', 'bold'); doc.text('Cliente:', M, y);
        doc.setFont('helvetica', 'normal'); doc.text(unCliente.nombre || '', M + 18, y);
        if (unCliente.direccion) { y += 5; doc.text(unCliente.direccion, M + 18, y); }
        y += 10;
    }
    doc.setFont('helvetica', 'bold'); doc.text('Detalle', M, y); doc.text('Importe', D, y, { align: 'right' });
    y += 3; doc.line(M, y, D, y); y += 7;

    orden.forEach((p, i) => {
        // Encabezado de cada presupuesto
        cabe(16);
        doc.setFont('helvetica', 'bold'); doc.setFontSize(10);
        const titulo = unCliente ? `${fechaCorta(p.fecha)} · N° ${p.numero || ''}`
            : `${fechaCorta(p.fecha)} · ${p.cliente?.nombre || ''} · N° ${p.numero || ''}`;
        doc.text(doc.splitTextToSize(titulo, D - M), M, y); y += 5;
        if (!unCliente && p.cliente?.direccion) { doc.setFont('helvetica', 'normal'); doc.setTextColor(110); doc.text(p.cliente.direccion, M, y); doc.setTextColor(0); y += 5; }
        y += 1; doc.setFont('helvetica', 'normal');
        (p.items || []).forEach(it => {
            const lineas = doc.splitTextToSize(String(it.desc || ''), 130);
            cabe(lineas.length * 5 + 2);
            doc.text(lineas, M + 4, y); doc.text(fmt(it.precio), D, y, { align: 'right' });
            y += lineas.length * 5 + 2;
        });
        if (p.notas && p.notas.trim()) {
            const n = doc.splitTextToSize(p.notas.trim(), D - M - 4);
            cabe(n.length * 4.5 + 2);
            doc.setFontSize(9); doc.setTextColor(110); doc.text(n, M + 4, y); doc.setTextColor(0); doc.setFontSize(10);
            y += n.length * 4.5 + 1;
        }
        if (orden.length > 1) {
            cabe(8);
            doc.setFont('helvetica', 'bold'); doc.text('Subtotal', 140, y, { align: 'right' }); doc.text(fmt(p.total), D, y, { align: 'right' });
            y += 4;
        }
        if (i < orden.length - 1) { y += 2; doc.setDrawColor(225); doc.line(M, y, D, y); doc.setDrawColor(200); y += 7; }
    });

    cabe(14);
    y += 3; doc.line(M, y, D, y); y += 8;
    doc.setFont('helvetica', 'bold'); doc.setFontSize(12);
    doc.text(orden.length > 1 ? `Total (${orden.length} presupuestos)` : 'Total', M, y);
    doc.text(fmt(orden.reduce((t, p) => t + (Number(p.total) || 0), 0)), D, y, { align: 'right' });

    const nom = unCliente ? `-${String(unCliente.nombre || '').toLowerCase().replace(/\s+/g, '-')}` : '';
    const ultimo = orden[orden.length - 1].fecha;
    doc.save(`presupuestos${nom}-${orden[0].fecha}${ultimo !== orden[0].fecha ? `-al-${ultimo}` : ''}.pdf`);
}

function dibujarPresupuesto(doc, p, cliente) {
    const M = 18; let y = 22;
    doc.setFont('helvetica', 'bold'); doc.setFontSize(18); doc.text(`Presupuesto${p.numero ? ` N° ${p.numero}` : ''}`, M, y);
    doc.setFont('helvetica', 'normal'); doc.setFontSize(10);
    doc.text(fechaCorta(p.fecha), 210 - M, y, { align: 'right' });
    y += 8; doc.setFont('helvetica', 'bold'); doc.text(p.emisor || '', M, y);
    if (p.contacto) { y += 5; doc.setFont('helvetica', 'normal'); doc.text(p.contacto, M, y); }
    y += 10; doc.setDrawColor(200); doc.line(M, y, 210 - M, y); y += 8;
    doc.setFont('helvetica', 'bold'); doc.text('Cliente:', M, y);
    doc.setFont('helvetica', 'normal'); doc.text(cliente.nombre, M + 18, y);
    if (cliente.direccion) { y += 5; doc.text(cliente.direccion, M + 18, y); }
    y += 12;
    doc.setFont('helvetica', 'bold'); doc.text('Detalle', M, y); doc.text('Importe', 210 - M, y, { align: 'right' });
    y += 3; doc.line(M, y, 210 - M, y); y += 6; doc.setFont('helvetica', 'normal');
    (p.items || []).forEach(it => {
        if (y > 270) { doc.addPage(); y = 22; }
        const lineas = doc.splitTextToSize(it.desc, 130);
        doc.text(lineas, M, y); doc.text(fmt(it.precio), 210 - M, y, { align: 'right' });
        y += lineas.length * 5 + 3;
    });
    doc.line(M, y, 210 - M, y); y += 7;
    doc.setFont('helvetica', 'bold'); doc.setFontSize(12);
    doc.text('Total', M, y); doc.text(fmt(p.total), 210 - M, y, { align: 'right' });
    if (p.notas && p.notas.trim()) {
        y += 12; doc.setFont('helvetica', 'normal'); doc.setFontSize(10);
        doc.text(doc.splitTextToSize(p.notas.trim(), 174), M, y);
    }
}

/**
 * Editor: nuevo (sin `inicial`), editar (`inicial` con id) o duplicar (`inicial` sin id).
 * Guarda en el sistema y baja el PDF.
 */
export default function PresupuestoPropio({ cliente, inicial, onCerrar, onGuardado }) {
    const { usuario } = useAuth();
    const editando = !!inicial?.id;
    const [yo, setYo] = useState(() => inicial?.emisor || leer('presu_propio_nombre', usuario?.nombre || ''));
    const [contacto, setContacto] = useState(() => inicial?.contacto || leer('presu_propio_contacto'));
    const [items, setItems] = useState(() => inicial?.items?.length
        ? inicial.items.map(it => ({ desc: it.desc, precio: String(Math.round(Number(it.precio) || 0)) }))
        : [{ desc: '', precio: '' }]);
    const [notas, setNotas] = useState(inicial ? (inicial.notas || '') : 'Validez: 7 días.');
    const [guardando, setGuardando] = useState(false);
    const total = items.reduce((s, it) => s + soloNumero(it.precio), 0);
    const setItem = (i, k, v) => setItems(prev => prev.map((it, j) => j === i ? { ...it, [k]: v } : it));
    const quitar = i => setItems(prev => prev.length > 1 ? prev.filter((_, j) => j !== i) : prev);

    const guardar = async () => {
        const validos = items.filter(it => it.desc.trim()).map(it => ({ desc: it.desc.trim(), precio: soloNumero(it.precio) }));
        if (!validos.length) { toast.error('Agregá al menos un ítem'); return; }
        if (!yo.trim()) { toast.error('Poné tu nombre'); return; }
        guardarLS('presu_propio_nombre', yo.trim()); guardarLS('presu_propio_contacto', contacto.trim());
        const datos = { items: validos, notas, emisor: yo.trim(), contacto: contacto.trim() };
        setGuardando(true);
        try {
            const r = editando
                ? await api.put(`/mis-presupuestos/${inicial.id}`, datos)
                : await api.post(`/mis-clientes/${cliente.id}/presupuestos`, datos);
            generarPDF(r.data, cliente);
            toast.success(editando ? 'Presupuesto actualizado' : `Presupuesto N° ${r.data.numero} guardado`);
            onGuardado && onGuardado(r.data);
        } catch (e) {
            toast.error(e?.response?.data?.mensaje || 'No se pudo guardar el presupuesto');
        } finally { setGuardando(false); }
    };

    return (
        <div className="fixed inset-0 z-[3000] flex items-end md:items-center md:justify-center bg-black/50 md:pl-[calc(var(--modal-sb,0px)+1.5rem)] md:pr-6" onClick={onCerrar}>
            <div className="w-full md:max-w-lg max-h-[calc(var(--vh,1vh)*92)] overflow-y-auto rounded-t-3xl md:rounded-3xl p-5 pb-8 bg-card space-y-3" onClick={e => e.stopPropagation()}>
                <div className="flex items-center justify-between">
                    <p className="text-body-lg font-black text-ink">
                        {editando ? `Presupuesto N° ${inicial.numero}` : inicial ? 'Copia de presupuesto' : 'Nuevo presupuesto'} · {cliente.nombre}
                    </p>
                    <button onClick={onCerrar} aria-label="Cerrar" className="w-9 h-9 rounded-xl bg-chip flex items-center justify-center text-muted shrink-0"><LuX size={16} /></button>
                </div>
                <p className="text-caption text-muted">Sale a tu nombre, sin la marca de Dispenser La Tienda. Queda guardado en tus clientes: solo lo ves vos.</p>
                {items.map((it, i) => (
                    <div key={i} className="flex gap-2">
                        <input value={it.desc} onChange={e => setItem(i, 'desc', e.target.value)} placeholder="Qué vas a hacer o vender" className={`${BASE} flex-1 min-w-0`} />
                        <input value={it.precio} onChange={e => setItem(i, 'precio', e.target.value)} placeholder="$" inputMode="numeric" className={`${BASE} w-28 shrink-0 text-right`} />
                        {items.length > 1 && (
                            <button onClick={() => quitar(i)} aria-label="Quitar ítem" className="w-10 h-10 rounded-xl bg-chip text-muted flex items-center justify-center shrink-0"><LuTrash2 size={14} /></button>
                        )}
                    </div>
                ))}
                <button onClick={() => setItems(p => [...p, { desc: '', precio: '' }])} className="h-9 px-3 rounded-xl bg-chip text-label font-bold text-secondary flex items-center gap-1">
                    <LuPlus size={14} /> Otro ítem
                </button>
                <div className="flex justify-between items-center px-1">
                    <span className="text-body font-black text-ink">Total</span>
                    <span className="text-title font-black text-ink">{fmt(total)}</span>
                </div>
                <textarea value={notas} onChange={e => setNotas(e.target.value)} rows={2} placeholder="Notas (validez, forma de pago...)" className={`${INPUT} h-auto py-2 resize-none`} />
                <div className="grid grid-cols-2 gap-2">
                    <input value={yo} onChange={e => setYo(e.target.value)} placeholder="Tu nombre" className={INPUT} />
                    <input value={contacto} onChange={e => setContacto(e.target.value)} placeholder="Tu teléfono / mail" className={INPUT} />
                </div>
                <button onClick={guardar} disabled={guardando} className="w-full py-3.5 rounded-2xl font-black text-label uppercase text-white bg-brand-red active:scale-[0.98] disabled:opacity-50">
                    {guardando ? 'Guardando…' : 'Guardar y bajar PDF'}
                </button>
            </div>
        </div>
    );
}
